import React from 'react';
import { Bell, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { notificationService } from '../services/notificationService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../hooks/use-toast';

export default function NotificationsBell() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data: notificationData, isLoading: loading } = useQuery({
        queryKey: ['notifications', { is_read: false, limit: 10 }],
        queryFn: () => notificationService.getAll({ is_read: false, limit: 10 })
    });

    const markReadMutation = useMutation({
        mutationFn: (id) => notificationService.markRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const markAllReadMutation = useMutation({
        mutationFn: () => notificationService.markAllRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            toast({ description: "All notifications marked as read." });
        }
    });

    const notifications = notificationData?.data || [];
    const unreadCount = notificationData?.pagination?.total || 0;

    const handleMarkRead = (id) => {
        markReadMutation.mutate(id);
    };

    const handleMarkAllRead = () => {
        markAllReadMutation.mutate();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs rounded-full bg-red-600 text-white">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex justify-between items-center">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                        <Button variant="link" size="sm" className="p-0 h-auto" onClick={handleMarkAllRead} disabled={markAllReadMutation.isPending}>
                            {markAllReadMutation.isPending ? "Marking..." : "Mark all as read"}
                        </Button>
                    )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {loading ? (
                     <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
                ) : notifications.length === 0 ? (
                    <DropdownMenuItem disabled className="text-center text-gray-500">No unread notifications</DropdownMenuItem>
                ) : (
                    notifications.map((notif) => (
                        <DropdownMenuItem key={notif.id || notif._id} className="flex items-start space-x-2 cursor-pointer" onSelect={(e) => e.preventDefault()}>
                            <div className="flex-1 text-sm whitespace-normal">
                                <p>{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-auto text-gray-400 hover:text-brand-600"
                                onClick={() => handleMarkRead(notif.id || notif._id)}
                                disabled={markReadMutation.isPending}
                                title="Mark as read"
                            >
                                <Check className="h-4 w-4" />
                            </Button>
                        </DropdownMenuItem>
                    ))
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
